<template>
  <v-container class="mt-10">
    <div id="jot-code" />
    <a class="anchor" id="webinar"></a>
    <div class="col text-center">
      <h2 class="text-center text-uppercase">
        <i class="fas fa-square"></i>
        {{ pageSettings.webinarTitle }}
      </h2>
    </div>
    <p
      class="text-justify"
      v-html="getRichTextString(pageSettings.webinarText, false)"
    ></p>
    <v-row class="mb-2">
      <v-col cols="12" md="7">
        <v-text-field
          :label="$t('form.fields.webinarCode') + ' *'"
          outlined
          v-model="webinarCode"
          required
          :error-messages="validCode ? '' : $t('form.fields.invalidCode')"
          :loading="loading"
        >
          <template #append-outer>
            <v-btn
              class="mt-n2"
              color="primary"
              :disabled="!webinarCode"
              @click="validateWebinarCode"
              :loading="loading"
            >
              {{ $t("form.btn.attend") }}
            </v-btn>
          </template>
        </v-text-field>
      </v-col>
      <v-col>
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header>
              {{ $t("form.fields.forgotCode") }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <validation-observer v-slot="{ invalid }">
                <validation-provider
                  :name="$t('form.fields.registerMail')"
                  v-slot="{ errors }"
                  rules="required|email"
                >
                  <v-text-field
                    :label="$t('form.fields.registerMail')"
                    outlined
                    v-model="registerMail"
                    required
                    :hint="mailHintText"
                    :error-messages="mailErrorText || errors"
                    persistent-hint
                    :loading="loading"
                  >
                    <template #append-outer>
                      <v-btn
                        class="mt-n2"
                        color="primary"
                        :disabled="invalid"
                        @click="requestWebinarCode"
                        :loading="loading"
                      >
                        {{ $t("form.btn.request") }}
                      </v-btn>
                    </template>
                  </v-text-field>
                </validation-provider>
              </validation-observer>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <v-dialog
      v-model="webinarWindow"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <div class="webinarWindow">
        <v-card>
          <v-toolbar dark color="primary">
            <v-btn icon dark @click="closeWebinarWindow">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title>Webinar</v-toolbar-title>
            <v-spacer />
            <v-btn
              v-if="pageSettings.jotFormKey"
              @click="showJotFormDialog = true"
              outlined
            >
              {{ $t("form.btn.askAQuestion") }}
            </v-btn>
            <v-btn
              v-else-if="webinars.length > 0 || pageSettings.slidoKey"
              @click="showSlidoDialog = true"
              outlined
            >
              {{ $t("form.btn.askAQuestion") }}
            </v-btn>
            <template v-if="webinars.length > 0">
              <v-spacer />
              <v-select
                :items="webinars"
                item-text="title"
                item-value="value"
                v-model="currentWebinarSite"
                outlined
                dense
                class="mt-5"
                @change="
                  () =>
                    $refs.webinarFrame.setAttribute(
                      'src',
                      webinars[currentWebinarSite].url
                    )
                "
              />
            </template>
          </v-toolbar>

          <v-card-text v-if="activeWebinar" class="webinar-card">
            <template v-if="webinars.length">
              <v-tabs v-model="currentWebinarTab">
                <v-tab>
                  {{ $t("schedule") }}
                </v-tab>
                <v-tab> Webinar </v-tab>
              </v-tabs>
              <v-tabs-items v-model="currentWebinarTab">
                <v-tab-item>
                  <webinar-schedule-view
                    :show-video="false"
                    :title="webinars[currentWebinarSite].title"
                    :room="webinars[currentWebinarSite].room"
                    class="webinar-schedule"
                  ></webinar-schedule-view>
                </v-tab-item>
                <v-tab-item>
                  <iframe
                    ref="webinarFrame"
                    :src="webinars[currentWebinarSite].url"
                  ></iframe>
                </v-tab-item>
              </v-tabs-items>
            </template>
            <iframe ref="webinarFrame" v-else></iframe>
          </v-card-text>
          <v-card-text class="webinar-card" v-else>
            <webinar-schedule-view
              :show-video="true"
              class="webinar-schedule"
            ></webinar-schedule-view>
          </v-card-text>
          <infinite-slide-bar
            duration="80s"
            class="webinar-sponsor-slider"
            v-if="sponsors.length"
          >
            <div class="webinar-sponsors">
              <div
                v-for="sponsor in sponsors"
                :key="sponsor.id"
                class="float-left ma-2"
              >
                <img
                  :src="sponsor.image._urls['one-half']"
                  height="40px"
                  contain
                />
              </div>
            </div>
          </infinite-slide-bar>
        </v-card>
      </div>
    </v-dialog>
    <v-dialog width="700" height="700" v-model="showJotFormDialog">
      <iframe
        :id="`JotFormIFrame-${pageSettings.jotFormKey}`"
        :title="$t('form.btn.askAQuestion')"
        allowtransparency="true"
        allowfullscreen="true"
        allow="geolocation; microphone; camera"
        :src="`https://form.jotform.com/${pageSettings.jotFormKey}`"
        frameborder="0"
        style="width: 1px; min-width: 100%; height: 580px; border: none"
        scrolling="no"
      ></iframe>
    </v-dialog>
    <v-dialog width="700" height="700" v-model="showSlidoDialog">
      <iframe
        :id="`slidoIFrame-${webinars[currentWebinarSite].slidoUrl}`"
        :title="$t('form.btn.askAQuestion')"
        allowtransparency="true"
        allowfullscreen="true"
        allow="geolocation; microphone; camera"
        :src="webinars[currentWebinarSite].slidoUrl"
        frameborder="0"
        style="width: 1px; min-width: 100%; height: 580px; border: none"
        scrolling="no"
        v-if="webinars.length"
      ></iframe>
      <iframe
        :id="`slidoIFrame-${pageSettings.slidoKey}`"
        :title="$t('form.btn.askAQuestion')"
        allowtransparency="true"
        allowfullscreen="true"
        allow="geolocation; microphone; camera"
        :src="`https://app.sli.do/event/${pageSettings.slidoKey}`"
        frameborder="0"
        style="width: 1px; min-width: 100%; height: 580px; border: none"
        scrolling="no"
        v-else
      ></iframe>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import {
  validateWebinarCodeResource,
  webinarScheduleResource,
  requestWebinarCodeResource,
  sponsorGroupResource,
} from "../api/resources";
import WebinarScheduleView from "./WebinarScheduleView";
import InfiniteSlideBar from "vue-infinite-slide-bar";
import * as moment from "moment";

export default {
  async mounted() {
    let allSponsors = [];
    if (this.pageSettings.sponsorGroupsIds) {
      for (const sponsorGroupId of this.pageSettings.sponsorGroupsWebinarIds) {
        const sponsors = (
          await sponsorGroupResource.get(sponsorGroupId, {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          })
        ).data.sponsors.filter((sponsor) => sponsor.approved);
        allSponsors = allSponsors.concat(sponsors);
      }
    }
    this.sponsors = allSponsors.sort(() => Math.random() - 0.5);
  },
  data: () => ({
    loading: false,
    validCode: true,
    webinarCode: null,
    webinarWindow: false,
    activeWebinar: true,
    registerMail: null,
    mailHintText: null,
    mailErrorText: null,
    showJotFormDialog: false,
    showSlidoDialog: false,
    currentWebinarSite: 0,
    currentWebinarTab: 0,
    participant: {},
    sponsors: [],
  }),
  components: {
    InfiniteSlideBar,
    WebinarScheduleView,
  },
  computed: {
    ...mapGetters(["pageSettings"]),
    webinarSite() {
      if (this.pageSettings.webinarLicenseKey) {
        return `https://livewebinar.com/${this.pageSettings.webinarLicenseKey}?_nickname=${this.participant.firstname} ${this.participant.lastname}`;
      } else if (this.pageSettings.yourVideoLicenseKey) {
        return `https://archiv.yourvideo.tv/watch/iframe.php?v=${this.pageSettings.yourVideoLicenseKey}`;
      }
      return "";
    },
    webinars() {
      return this.pageSettings.webinars
        ? this.pageSettings.webinars.map((webinar, index) => ({
            title: webinar.title,
            room: webinar.room,
            value: index,
            url: `https://archiv.yourvideo.tv/watch/iframe.php?v=${webinar.yourVideoLicenseKey}`,
            slidoUrl: `https://app.sli.do/event/${webinar.slidoEventId}`,
          }))
        : [];
    },
  },
  methods: {
    async validateWebinarCode() {
      this.loading = true;
      this.validCode = true;
      const response = await validateWebinarCodeResource
        .post(
          "",
          {
            webinarCode: this.webinarCode,
          },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        )
        .catch(() => {
          this.validCode = false;
        });
      if (this.validCode) {
        this.participant = response.data;
        const events = (
          await webinarScheduleResource.get("", {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          })
        ).data.results;
        if (events.length > 0) {
          this.activeWebinar = moment().isBefore(
            moment(events[events.length - 1].date).add(1, "d")
          );
          /*   if (this.activeWebinar) {
            let startDate = moment(events[0].date);
            if (
              !this.participant.selectedPackages.find((element) =>
                element.key.includes("tunneltag")
              )
            ) {
              startDate = startDate.add(1, "day");
            }
            this.activeWebinar = moment().isAfter(startDate);
          }*/
        }
        this.webinarWindow = true;
        const navigationItems = document.getElementsByTagName("nav");
        if (navigationItems.item(0)) {
          navigationItems.item(0).classList.add("d-none");
        }

        this.$nextTick(() => {
          if (this.webinars.length > 0) {
            this.$refs.webinarFrame.setAttribute(
              "src",
              this.webinars[this.currentWebinarSite].url
            );
          } else {
            this.$refs.webinarFrame.setAttribute("src", this.webinarSite);
          }
        });
      }
      this.loading = false;
    },
    async requestWebinarCode() {
      this.loading = true;
      let validMail = true;
      await requestWebinarCodeResource
        .post(
          "",
          {
            mail: this.registerMail,
          },
          {
            headers: {
              "x-xsrf-token": this.$cookies.get("oegg-cms.csrf"),
            },
          }
        )
        .catch(() => {
          validMail = false;
        });
      if (validMail) {
        this.mailHintText = this.$t("webinarCodeSendSuccess", {
          mail: this.registerMail,
        });
        this.mailErrorText = null;
      } else {
        this.mailHintText = null;
        this.mailErrorText = this.$t("webinarCodeSendFailure", {
          mail: this.registerMail,
        });
      }
      this.loading = false;
    },
    closeWebinarWindow() {
      this.webinarWindow = false;
      const navigationItems = document.getElementsByTagName("nav");
      if (navigationItems.item(0)) {
        navigationItems.item(0).classList.remove("d-none");
      }
    },
    getRichTextString(richText, stripHtmlTags) {
      let htmlText = "";
      if (!richText || !richText.items) {
        return htmlText;
      }
      htmlText = richText.items.map((element) => element.content).join();
      if (stripHtmlTags) {
        const tmp = document.createElement("DIV");
        tmp.innerHTML = htmlText;
        return tmp.textContent || tmp.innerText || "";
      }
      return htmlText;
    },
  },
};
</script>

<style scoped>
.webinar-sponsors {
  display: flex;
  justify-content: space-around;
}
.webinar-card {
  min-height: calc(100vh - 65px);
}
.webinar-schedule {
  margin-bottom: 65px;
}
</style>
